// ---------------------------------------------------------------------------
// V23 — WHAT A PAY ROW SAYS, AS A PURE FUNCTION.
//
// `system/proof.ts`'s shape and reason: the sentence a row is drawn with is the thing most
// worth unit-testing and the thing least testable inside a component.
//
// ---------------------------------------------------------------------------
// NOTHING HERE COMPUTES A FIGURE
//
// `13-…` §5: two engines that round differently produce different pay for identical work.
// A browser deriving a gross from a rate and some hours would be the second engine, and it
// would be wrong in a way nobody could see until a payslip arrived. So every function
// below FORMATS or LABELS; `verify-V23.sh` greps this file for arithmetic operators after
// stripping comments, and the only division that survives is the one turning minor units
// into a string.
//
// AND STATUS IS NEVER A COLOUR ALONE. Every state returns a tone AND words, because
// `11-console-design-system.md` requires it and because the person reading this is a
// finance manager scanning forty rows for the two that did not settle.
// ---------------------------------------------------------------------------

import type { Translate as CatalogueTranslate } from "../i18n/translate";

/** S145. The labels below are drawn in the reader's language, so the screen hands over its `t`. */
type Translate = CatalogueTranslate;

export type ChipTone = "live" | "attention" | "failed" | "neutral";

export type PayRow = {
  timesheet_id: string;
  worker_id: string;
  worker_name: string;
  role_name: string;
  zone: string | null;
  payable_minutes: string | null;
  currency: string | null;
  pay_calculation_id: string | null;
  pay_gross_minor: string | null;
  charge_calculation_id: string | null;
  /** 0164: a charge is in the requirement's charge currency, which may not be the pay's. */
  charge_currency?: string | null;
  charge_gross_minor: string | null;
  margin_minor: string | null;
  floor_uplift_minor: string | null;
  is_priced: boolean;
};

export type BreakdownLine = {
  seq: number;
  rule_key: string;
  rule_order: number;
  band: string;
  effect_kind: string;
  label: string;
  amount_minor: string;
  running_minor: string;
  detail: Record<string, unknown> | null;
};

export type BatchRow = {
  id: string;
  reference: string;
  provider: string;
  /** S37. The provider's name, so the Rail column does not print an id. */
  provider_name?: string;
  currency: string;
  line_count: number;
  total_minor: string;
  is_sandbox: boolean;
  is_superseded: boolean;
  is_reconciled: boolean | null;
  settled_count: number;
  failed_count: number;
  unreported_count: number;
  mismatch_count: number;
};

/**
 * Minor units to something a person reads — `system/format.ts`'s, the only one (S38).
 *
 * INTEGER STRING WORK, NOT DIVISION. `(minor / 100).toFixed(2)` is a binary float and
 * drops a trailing zero on the way through JSON — the portfolio note about a grep for
 * `778899.00` that could never fire. The value arrives as a STRING because it is a
 * `bigint` in Postgres, and it stays one; format.ts moves the point and asks the
 * currency how far, which the copy that lived here assumed was two.
 */
export { money } from "./format";

/** Minutes to hours and minutes, with the minute total in brackets (format.ts). */
export { minutesLong as duration } from "./format";

/**
 * WHETHER A SHIFT HAS BEEN PRICED, AND WHETHER ANYBODY SHOULD LOOK AT IT.
 *
 * `attention` on a statutory top-up is the important one. §4.1: the uplift is "recorded
 * explicitly, never silent" — and a screen that showed the topped-up total without saying
 * a top-up happened would be the silent version of exactly the thing the specification
 * forbids. It is not an error, so it is not red; it is a fact somebody should know.
 */
export function payState(row: PayRow, t: Translate): { tone: ChipTone; label: string } {
  if (!row.is_priced) return { tone: "neutral", label: t("Not priced yet") };
  if (row.floor_uplift_minor !== null && row.floor_uplift_minor !== "0") {
    return { tone: "attention", label: t("Topped up to the statutory minimum") };
  }
  if (row.charge_calculation_id === null) {
    return { tone: "attention", label: t("Priced for pay, not yet for charge") };
  }
  return { tone: "live", label: t("Priced") };
}

/**
 * WHAT A BATCH IS DOING.
 *
 * `unreported` IS ITS OWN STATE AND NOT "not settled". A line the provider has said
 * nothing about raises no error and produces no alert — the money simply never arrives —
 * and "we do not know" prompts a different action at 09:00 on a Monday from "it failed".
 */
export function batchState(row: BatchRow, t: Translate): { tone: ChipTone; label: string } {
  if (row.is_superseded) return { tone: "attention", label: t("Superseded by a later batch") };
  if (row.failed_count > 0) return { tone: "failed", label: t("{n} payment(s) failed", { n: row.failed_count }) };
  if (row.mismatch_count > 0) {
    return { tone: "failed", label: t("{n} payment(s) settled for the wrong amount", { n: row.mismatch_count }) };
  }
  if (row.unreported_count > 0) {
    return { tone: "attention", label: t("{n} payment(s) not yet reported on", { n: row.unreported_count }) };
  }
  if (row.is_reconciled === true) return { tone: "live", label: t("Reconciled — every line settled") };
  return { tone: "neutral", label: t("In flight") };
}

/**
 * The sandbox warning, as words rather than as a colour.
 *
 * D10's boundary is the single most important thing a person can misread on this screen.
 * A batch that went to a sandbox looks exactly like one that went to a bank, and somebody
 * who believes their crew has been paid when nothing moved will find out from the crew.
 */
export function railNote(row: BatchRow, t: Translate): { tone: ChipTone; label: string } | null {
  if (!row.is_sandbox) return null;
  return { tone: "attention", label: t("Sandbox rail — no money left the account") };
}
